namespace $.$$ {

	export type $easyreels_user_recorder_mode = 'off' | 'live' | 'sheet'

	const sheet_phases: readonly $easyreels_user_recorder_phase[] = ['stopping', 'uploading', 'finishing', 'assembling', 'ready', 'failed']
	const linked_phases: readonly $easyreels_user_recorder_phase[] = ['assembling', 'ready']

	export class $easyreels_user_recorder extends $.$easyreels_user_recorder {

		@$mol_mem
		session() {
			const view = this

			return new (class extends $easyreels_user_recorder_session {
				override frame() {
					return view.frame()
				}

				override async present(stream: MediaStream) {
					await view.present(stream)
				}
			})()
		}

		phase() {
			return this.session().phase()
		}

		video() {
			return this.Preview().dom_node() as HTMLVideoElement
		}

		frame() {
			const video = this.video()
			return { width: video.videoWidth, height: video.videoHeight }
		}

		async present(stream: MediaStream) {
			const video = this.video()
			if (video.srcObject !== stream) video.srcObject = stream
			await video.play().catch(() => {})
		}

		camera_on() {
			return this.session().media.camera() !== null
		}

		override stream() {
			return this.session().media.camera()
		}

		override level() {
			return this.session().media.level()
		}

		override preview_transform() {
			return this.session().media.facing() === 'user' ? 'scaleX(-1)' : 'none'
		}

		override preview_resize(event?: Event) {
			if (event) this.session().orientation_changed()
			return null
		}

		override mode(): $easyreels_user_recorder_mode {
			const phase = this.phase()

			if (sheet_phases.includes(phase) || (phase === 'error' && this.session().retryable())) return 'sheet'
			if (phase === 'recording' || this.camera_on()) return 'live'

			return 'off'
		}

		override layers() {
			switch (this.mode()) {
				case 'live': return [this.Preview(), this.Top(), this.Bottom(), ...this.devices_open() && this.camera_on() ? [this.Devices()] : []]
				case 'sheet': return [this.Preview(), this.Top(), this.Sheet()]
				case 'off': return [this.Preview(), this.Off(), this.Top()]
			}
		}

		override top() {
			if (this.mode() !== 'live') return [this.Top_row()]

			const session = this.session()

			return [
				this.Top_row(),
				...this.camera_on() ? [this.Facing()] : [],
				...session.uploader.warning() && !session.idle() ? [this.Warning()] : [],
				...session.limit() ? [this.Limit()] : [],
				...session.error() ? [this.Error()] : [],
				...this.status() ? [this.Status()] : [],
			]
		}

		override top_row() {
			return this.mode() === 'live' ? [this.Badge(), this.Tools()] : [this.Tools()]
		}

		override tool_list() {
			return this.mode() === 'live' && this.camera_on() ? [this.Settings(), ...this.tools()] : this.tools()
		}

		override devices_lines() {
			return [
				this.Camera_field(),
				this.Mic_field(),
				...this.live() ? [this.Mic_note()] : [],
				this.Devices_close(),
			]
		}

		device_options(kind: $easyreels_user_recorder_device_kind, fallback: string) {
			const devices = this.session().media.devices_of(kind).filter(device => device.deviceId)

			return Object.fromEntries(devices.map((device, index) => [device.deviceId, device.label || this.text(fallback, { number: index + 1 })]))
		}

		override camera_options() {
			return this.device_options('videoinput', 'camera_number')
		}

		override mic_options() {
			return this.device_options('audioinput', 'mic_number')
		}

		override camera_device(next?: string) {
			if (next !== undefined) this.session().choose_camera(next)
			return this.session().media.camera_id()
		}

		override mic_device(next?: string) {
			if (next !== undefined) this.session().choose_mic(next)
			return this.session().media.mic_id()
		}

		override camera_select_enabled() {
			return this.switch_enabled()
		}

		override mic_select_enabled() {
			return this.session().idle() && !this.session().busy()
		}

		override devices_close(event?: Event) {
			if (event) this.devices_open(false)
			return null
		}

		override bottom() {
			return [this.Meter(), this.Controls()]
		}

		override off_lines() {
			const session = this.session()

			return [
				this.Status(),
				...session.limit() ? [this.Limit()] : [],
				...session.error() ? [this.Error()] : [],
				this.Preview_button(),
			]
		}

		override sheet_lines() {
			const session = this.session()
			const phase = this.phase()

			return [
				this.Summary(),
				this.Status(),
				...session.error() ? [this.Error()] : [],
				...session.results().length ? [this.Segments()] : [],
				...linked_phases.includes(phase) && this.links().length ? [this.Links()] : [],
				...this.sheet_controls().length ? [this.Sheet_controls()] : [],
			]
		}

		override sheet_controls() {
			const phase = this.phase()

			if (phase === 'error') return [this.Retry()]
			if (phase === 'ready' || phase === 'failed') return [this.Again()]

			return []
		}

		override live() {
			return this.phase() === 'recording'
		}

		clock() {
			const seconds = this.session().elapsed()
			return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`
		}

		text(key: string, params?: $easyreels_i18n_params) {
			return $easyreels_i18n_text(`$easyreels_user_recorder_${key}`, params)
		}

		override summary() {
			return this.text('summary', { badge: this.badge(), clock: this.clock() })
		}

		override badge() {
			switch (this.phase()) {
				case 'recording': return this.text('badge_rec', { clock: this.clock() })
				case 'starting': return this.text('badge_starting')
				case 'stopping':
				case 'uploading':
				case 'finishing': return this.text('badge_saving')
				case 'assembling': return this.text('badge_assembling')
				case 'ready': return this.text('badge_saved')
				case 'failed': return this.text('badge_failed')
				default: return this.text(this.camera_on() ? 'badge_preview' : 'badge_off')
			}
		}

		override facing_hint() {
			switch (this.session().media.facing()) {
				case 'user': return this.text('facing_user')
				case 'environment': return this.text('facing_environment')
				default: return this.text('facing_other')
			}
		}

		override warning_detail() {
			const queue = this.session().uploader.queue()

			return $easyreels_i18n_plural('$easyreels_user_recorder_warning_detail', queue.count, { bytes: $easyreels_i18n_bytes(queue.bytes) })
		}

		override warning_problem() {
			const problem = this.session().uploader.problem()

			return problem ? this.text('last_error', { error: $easyreels_i18n_note_line(problem) }) : ''
		}

		override warning_lines() {
			const lines = [this.Warning_title(), this.Warning_detail()]

			return this.warning_problem() ? [...lines, this.Warning_problem()] : lines
		}

		override limit_detail() {
			return this.session().limit()?.text() ?? ''
		}

		override status() {
			const session = this.session()

			switch (this.phase()) {
				case 'idle':
				case 'limit':
					return this.camera_on() ? '' : this.text('status_preview_off')
				case 'starting': return this.text('status_starting')
				case 'recording': return session.status()?.text() ?? ''
				case 'stopping': return this.text('status_stopping')
				case 'uploading': return $easyreels_i18n_plural('$easyreels_user_recorder_status_uploading', session.uploader.queue().count)
				case 'finishing': return this.text('status_finishing')
				case 'assembling': return this.text('status_assembling')
				case 'ready': return this.text('status_ready')
				case 'failed': return this.text('status_failed')
				case 'error': return this.text(session.retryable() ? 'status_error_retry' : 'status_error_start')
			}
		}

		override error() {
			return this.session().error()?.text() ?? ''
		}

		override error_detail() {
			return this.session().error()?.detail ?? ''
		}

		override error_lines() {
			return this.error_detail() ? [this.Error_text(), this.Error_detail()] : [this.Error_text()]
		}

		override controls() {
			const session = this.session()
			const phase = this.phase()
			const switcher = this.camera_on() && session.media.cameras() > 1 ? [this.Switch()] : []

			if (phase === 'recording') return [...switcher, this.Stop()]
			if (phase === 'starting') return []

			return [...switcher, this.Start()]
		}

		override ready_enabled() {
			return !this.session().busy()
		}

		override switch_enabled() {
			return !this.session().busy() && !this.session().switching()
		}

		override segment_links() {
			return this.session().results().map((_, index) => this.Segment_link(index))
		}

		result(index: number) {
			return this.session().results()[index]
		}

		override segment_uri(index: number) {
			return this.result(index).downloadURL ?? ''
		}

		override segment_title(index: number) {
			const segment = this.result(index)
			const camera = this.text(segment.capture.facingMode === 'environment' ? 'camera_back' : segment.capture.facingMode === 'user' ? 'camera_front' : 'camera_other')
			const seconds = Math.round((segment.endMs - segment.startMs) / 1000)

			return this.text('segment', {
				number: segment.index + 1,
				camera,
				size: `${segment.capture.width}x${segment.capture.height}`,
				seconds: $easyreels_i18n_seconds(seconds),
				bytes: $easyreels_i18n_bytes(segment.bytes),
			})
		}

		override preview_click(event?: Event) {
			if (event) this.session().preview()
			return null
		}

		override switch_click(event?: Event) {
			if (event) this.session().switch_camera()
			return null
		}

		override start_click(event?: Event) {
			if (event) this.session().start()
			return null
		}

		override stop_click(event?: Event) {
			if (event) void this.session().stop()
			return null
		}

		override retry_click(event?: Event) {
			if (event) this.session().retry()
			return null
		}

		override again_click(event?: Event) {
			if (event) this.session().again()
			return null
		}

	}

}
